import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  Grid,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { makeStyles } from '@mui/styles';
import clsx from 'clsx';
import { TFunction } from 'i18next';
import { FC, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { OrderListingType } from 'types/order';
import { usePreferences } from 'contexts/preferences-context';

import { CurrentData, FiltersType, getColumnList } from '../services';
import { flexRender, getCoreRowModel, SortingState, useReactTable } from "@tanstack/react-table";

import { arrayMove, SortableContext } from "@dnd-kit/sortable";
import { DndContext, type DragEndEvent } from "@dnd-kit/core";
import { OrderTableColumn } from './order-table-column';
import OrdersCards from './orders-card';

const useStyles = makeStyles((theme: any) => ({
  table: {
    borderSpacing: '0 8px',
    borderCollapse: 'separate',
  },
  headerRow: { cursor: 'default' },
  headerCell: { borderBottom: 'none' },
  row: { cursor: 'pointer' },
  cell: {
    borderTop: `1px solid ${theme.palette.button.default}`,
    borderBottom: `1px solid ${theme.palette.button.default}`,
  },
  firstCell: {
    borderLeft: `3px solid ${theme.palette.success.dark}`,
    borderTopLeftRadius: theme.spacing(0.5),
    borderBottomLeftRadius: theme.spacing(0.5),
  },
  lastCell: {
    borderRight: `1px solid ${theme.palette.button.default}`,
    borderTopRightRadius: theme.spacing(0.5),
    borderBottomRightRadius: theme.spacing(0.5),
  },
  paginationButton: {
    width: theme.spacing(4),
    height: theme.spacing(4),
  },
  pagintaionIcon: { fontSize: '0.98rem' },
  link: {
    textDecoration: "none", color: "inherit"
  }
}));

type OrdersTableProps = {
  t: TFunction;
  currentData: CurrentData;
  orders: OrderListingType[];
  handleSelectRow: (id: string, code: string) => void;
  handleTablePageChange: (action: 'prev' | 'next') => (cuantity?: number) => void;
  agencyId: string,
  filter: FiltersType
};

const OrdersTable: FC<OrdersTableProps> = ({
  t,
  currentData,
  orders,
  handleSelectRow,
  handleTablePageChange,
  agencyId,
  filter
}) => {
  const classes = useStyles();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { orderColumns, setOrderColumns } = usePreferences() as any;

  const baseColumns = useMemo(() => getColumnList({
    t,
    hidecolumns: {
      agency: agencyId !== '',
      status: filter !== "all"
    }
  }), [t, agencyId, filter]);

  const columnOrder = useMemo(() => {
    if (!orderColumns || orderColumns.length === 0) {
      return baseColumns.map((col) => col.accessorKey);
    }
    return orderColumns;
  }, [orderColumns, baseColumns]);

  const columns = useMemo(() => {
    if (columnOrder.length === 0) return baseColumns;

    const sorted = [...baseColumns].sort((columnA, columnB) => {
      const indexA = columnOrder.indexOf(columnA.accessorKey);
      const indexB = columnOrder.indexOf(columnB.accessorKey);

      if (indexA === -1) return 1;
      if (indexB === -1) return -1;

      return indexA - indexB;
    });

    return sorted;
  }, [columnOrder, baseColumns]);

  const [sorting, setSorting] = useState<SortingState>([]);
  const table = useReactTable({
    data: orders,
    columns,
    debugTable: false,
    getCoreRowModel: getCoreRowModel(),
    state: {
      sorting,
    },
    onSortingChange: setSorting,
  });

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      const currentOrder = columnOrder.length > 0 ? columnOrder : baseColumns.map((column) => column.accessorKey);

      const oldIndex = currentOrder.indexOf(active.id as string);
      const newIndex = currentOrder.indexOf(over.id as string);
      const newOrder = arrayMove(currentOrder, oldIndex, newIndex);

      setOrderColumns(newOrder);
    }
  };

  if (!orders.length) {
    return (
      <Grid container>
        <Grid item />
      </Grid>
    );
  }

  // Mobile: render compact cards
  if (isMobile) {
    return (
      <Grid container>
        <Grid item xs={12}>
          <OrdersCards
            t={t}
            orders={orders}
            handleSelectRow={handleSelectRow}
            agencyId={agencyId}
          />
        </Grid>

        {currentData.pages > 0 && (
          <Grid item xs={12}>
            <Grid container justifyContent="flex-end" alignItems="center" spacing={2}>
              <Grid item>
                {`Page: ${currentData.page} of ${currentData.pages}`}
              </Grid>

              <Grid item>
                <IconButton
                  disabled={currentData.page <= 1}
                  className={classes.paginationButton}
                  onClick={() => handleTablePageChange('prev')()}
                  size="large"
                >
                  <FontAwesomeIcon icon="chevron-left" className={classes.pagintaionIcon} />
                </IconButton>
              </Grid>

              <Grid item>
                <IconButton
                  disabled={currentData.page >= currentData.pages}
                  className={classes.paginationButton}
                  onClick={() => handleTablePageChange('next')()}
                  size="large"
                >
                  <FontAwesomeIcon icon="chevron-right" className={classes.pagintaionIcon} />
                </IconButton>
              </Grid>
            </Grid>
          </Grid>
        )}
      </Grid>
    );
  }

  // Desktop: render table
  return (
    <Grid container>
      <Grid item xs={12}>
        <DndContext onDragEnd={handleDragEnd}>
          <TableContainer>
            <Table className={classes.table}>
              <TableHead>
                {table.getHeaderGroups().map(headerGroup => (
                  <SortableContext key={headerGroup.id} items={headerGroup.headers}>
                    <TableRow key={headerGroup.id}>
                      {headerGroup.headers.map(header => (
                        <OrderTableColumn key={header.id} header={header} />
                      ))}
                    </TableRow>
                  </SortableContext>
                ))}
              </TableHead>

              <TableBody>
                {table.getRowModel().rows.map(row => {
                  return (
                    <TableRow
                      key={row.id}
                      hover
                      className={classes.row}
                      onClick={() => handleSelectRow(row.original._id, row.original.order_id)}
                    >
                      {row.getVisibleCells().map((cell, index) => {
                        return (
                          <TableCell
                            key={cell.id}
                            className={
                              clsx(classes.cell, {
                                [classes.firstCell]: index === 0,
                                [classes.lastCell]: index === row.getVisibleCells().length - 1,
                              })
                            }
                          >
                            <Link to={`/orders/details/${row.original._id}/overview`} className={classes.link}>
                              {flexRender(cell.column.columnDef.cell, cell.getContext())}
                            </Link>
                          </TableCell>
                        );
                      })}
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </DndContext>
      </Grid>

      <Grid item xs={12}>
        <Grid container justifyContent="flex-end" alignItems="center" spacing={2}>
          <Grid item>
            {`Page: ${currentData.page} of ${currentData.pages}`}
          </Grid>

          <Grid item>
            <IconButton
              disabled={currentData.page <= 1}
              className={classes.paginationButton}
              onClick={() => handleTablePageChange('prev')()}
              size="large"
            >
              <FontAwesomeIcon icon="chevron-left" className={classes.pagintaionIcon} />
            </IconButton>
          </Grid>

          <Grid item>
            <IconButton
              disabled={currentData.page >= currentData.pages}
              className={classes.paginationButton}
              onClick={() => handleTablePageChange('next')()}
              size="large"
            >
              <FontAwesomeIcon icon="chevron-right" className={classes.pagintaionIcon} />
            </IconButton>
          </Grid>
        </Grid>
      </Grid>
    </Grid>
  );
};

export default OrdersTable;
